<script setup>
import { computed, ref, watch, onUnmounted, nextTick, onMounted } from "vue";

// * Props
const props = defineProps({
  name: {
    type: String,
    required: true,
    default: "base",
  },
  visible: {
    type: Boolean,
    default: false,
  },
  modal: {
    type: Boolean,
    default: true,
  },
  closeButton: {
    type: Boolean,
    default: true,
  },
  closeOutside: {
    type: Boolean,
    default: true,
  },
  classes: {
    type: [String, Array],
    default: "base-modal",
  },
  beforeOpen: {
    type: Function,
    default: () => {},
  },
  beforeClose: {
    type: Function,
    default: () => {},
  },
  opened: {
    type: Function,
    default: () => {},
  },
  closed: {
    type: Function,
    default: () => {},
  },
});

// * Refs
const dialog = ref(null);
// * Data
const isOpened = defineModel("isOpened", {
  type: Boolean,
  default: false,
  get(value) {
    return value;
  },
  set(value) {
    console.log("### set", value);
    if (value) {
      // Ensure dialog element is mounted before showing
      if (dialog?.value) {
        _preShow();
        _postShow();
      } else {
        nextTick(() => {
          if (dialog?.value) {
            _preShow();
            _postShow();
          }
        });
      }
    } else {
      _preHide();
      _postHide();
    }
    return value;
  },
});

// * Computed
const elementID = computed(() => `${props.name}-modal`);

const classList = computed(() =>
  [].concat(["base-modal"], props.classes, {
    "base-modal--fullscreen": window?.innerWidth < 768 ?? false,
  }),
);

// * Scroll lock helpers (html + body)
let activeScrollLocks = 0;

const lockScroll = () => {
  if (typeof document === "undefined") return;
  if (activeScrollLocks === 0) {
    const html = document.documentElement;
    const body = document.body;
    html.dataset.prevOverflow = html.style.overflow || "";
    body.dataset.prevOverflow = body.style.overflow || "";
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
  }
  activeScrollLocks += 1;
};

const unlockScroll = () => {
  if (typeof document === "undefined") return;
  if (activeScrollLocks > 0) activeScrollLocks -= 1;
  if (activeScrollLocks === 0) {
    const html = document.documentElement;
    const body = document.body;
    html.style.overflow = html.dataset.prevOverflow || "";
    body.style.overflow = body.dataset.prevOverflow || "";
    delete html.dataset.prevOverflow;
    delete body.dataset.prevOverflow;
  }
};

const isScrollLocked = ref(false);
const lockScrollForInstance = () => {
  if (!isScrollLocked.value) {
    lockScroll();
    isScrollLocked.value = true;
  }
};
const unlockScrollForInstance = () => {
  if (isScrollLocked.value) {
    unlockScroll();
    isScrollLocked.value = false;
  }
};

// * Methods
const _preShow = () => {
  props.beforeOpen();

  lockScrollForInstance();

  console.log("show");
  if (props.modal) {
    dialog?.value?.showModal();
  } else {
    dialog?.value?.show();
  }
};

const _postShow = () => {
  emit("update:visible", isOpened.value);

  props.opened();
  // Schedule slide-up animation for tour modals after dialog is opened
  try {
    if (props?.name && typeof props.name === "string" && props.name.includes("tour")) {
      const el = dialog?.value;
      if (el) {
        el.classList.remove("is-open");
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.classList.add("is-open");
          });
        });
      }
    }
  } catch (e) {
    /* no-op */
  }
};

const show = () => {
  isOpened.value = true;
};

const _preHide = () => {
  props.beforeClose();

  console.log("hide");
  // Reset animation state for next open
  try {
    dialog?.value?.classList?.remove("is-open");
  } catch (e) {
    /* no-op */
  }
  dialog?.value?.close();
};

const _postHide = () => {
  unlockScrollForInstance();
  emit("close");
  emit("update:visible", isOpened.value);

  props.closed();
};

const hide = () => {
  onClose();
};
const onClick = (event) => {
  const rect = dialog?.value?.getBoundingClientRect();

  const isInDialog =
    rect.top <= event.clientY &&
    event.clientY <= rect.top + rect.height &&
    rect.left <= event.clientX &&
    event.clientX <= rect.left + rect.width;

  console.log("isInDialog", isInDialog);

  if (props.closeOutside && !isInDialog) {
    hide();
  }
};

const onClose = () => {
  console.log("### onClose:", isOpened.value);
  if (isOpened.value) isOpened.value = false;
};

// * Hooks
watch(
  () => props.visible,
  (value) => {
    console.log("### watch", value, isOpened.value);
    if (value !== isOpened.value) {
      isOpened.value = !!value;
    }
  },
);

onUnmounted(() => {
  // Safety: ensure scroll is unlocked if component unmounts while open
  unlockScrollForInstance();
});

// If visible is true on initial mount (no change event), open the modal
onMounted(() => {
  if (props.visible && !isOpened.value) {
    // Defer to ensure <dialog> is in DOM
    nextTick(() => {
      isOpened.value = true;
    });
  }
});

// * Emits
const emit = defineEmits(["close", "update:visible"]);

// * Expose
defineExpose({
  show,
  hide,
});
</script>

<template>
  <dialog
    :id="elementID"
    ref="dialog"
    :class="classList"
    role="alertdialog"
    aria-labelledby="modal-title"
    aria-modal="true"
    @click="onClick"
    @close="onClose">
    <slot></slot>
    <template v-if="closeButton">
      <div class="absolute right-0 top-0 sm:block">
        <button
          @click.stop="hide"
          class="rounded-md text-color-1 pr-4 pt-4 hover:text-brand hover:cursor-pointer focus:outline-none close-button">
          <span class="sr-only">Close</span>
          <svg
            class="h-3 w-3"
            viewBox="0 0 14 14"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            aria-hidden="true">
            <path
              clip-rule="evenodd"
              d="M8.41155 7.00032L13.6886 12.2822C14.079 12.6729 14.0787 13.306 13.688 13.6964C13.2973 14.0867 12.6641 14.0864 12.2738 13.6957L6.99628 8.41348L1.70658 13.6953C1.31577 14.0856 0.682602 14.0851 0.292368 13.6943C-0.0978661 13.3035 -0.0973954 12.6703 0.293419 12.2801L5.58271 6.99863L0.29565 1.70679C-0.094698 1.31609 -0.0944116 0.682921 0.296289 0.292573C0.68699 -0.0977741 1.32016 -0.0974878 1.7105 0.293213L6.99797 5.58547L12.2739 0.317343C12.6648 -0.0728905 13.2979 -0.0724199 13.6881 0.318395C14.0784 0.709209 14.0779 1.34237 13.6871 1.73261L8.41155 7.00032Z" />
          </svg>
        </button>
      </div>
    </template>
  </dialog>
</template>

<style>
:root {
  --base-modal-backdrop-color: var(--bg-1);
  --base-modal-backdrop-opacity: 0.85;
  --base-modal-bg-color: var(--bg-2);
  --base-modal-border: 1px solid var(--bg-3);
  --base-modal-border-radius: 10px;
  --base-modal-padding-y: 30px;
  --base-modal-padding-x: 30px;
  --base-modal-padding: var(--base-modal-padding-y) var(--base-modal-padding-x);
}

.base-modal {
  background: var(--base-modal-bg-color);
  border: var(--base-modal-border);
  border-radius: var(--base-modal-border-radius);
  width: min(600px, 100%);
  max-height: calc(100vh - env(safe-area-inset-bottom));
  padding: var(--base-modal-padding);
}

.base-modal::backdrop {
  background: var(--base-modal-backdrop-color);
}

/* * Transition config */
/*
  Modal panel, show/hide based on modal state.

  Entering: "ease-out duration-300"
    From: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
    To: "opacity-100 translate-y-0 sm:scale-100"
  Leaving: "ease-in duration-200"
    From: "opacity-100 translate-y-0 sm:scale-100"
    To: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
 */
/*   Closed state of the dialog   */
.base-modal {
  /* Base */
  @apply transform overflow-auto transition-all;
  /* To entering */
  @apply ease-out duration-300;
  @apply opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95;
}

/*   Open state of the dialog  */
.base-modal[open] {
  @apply ease-in duration-200 opacity-100 translate-y-0 sm:scale-100;
}

/*
  Background backdrop, show/hide based on modal state.

  Entering: "ease-out duration-300"
    From: "opacity-0"
    To: "--base-modal-backdrop-opacity"
  Leaving: "ease-in duration-200"
    From: "--base-modal-backdrop-opacity"
    To: "opacity-0"
  */
.base-modal::backdrop {
  @apply transition-opacity duration-300 ease-out opacity-0;
}

.base-modal[open]::backdrop {
  @apply duration-200 ease-in;
  opacity: var(--base-modal-backdrop-opacity);
}
.close-button {
  color: var(--text-1);
}
.close-button:hover {
  color: var(--brand-hover);
  cursor: pointer;
}
.close-button:focus {
  outline: none;
}
/*   Slide-up behavior for tour modals on all viewports (pure CSS)   */
/* Target tour dialogs: id contains '-tour' and ends with '-modal' */
.base-modal[id$="-modal"][id*="-tour"] {
  margin: 0;
  width: min(600px, 100%);
  max-width: min(600px, 100%);
  /* Bottom-sheet look */
  border-radius: 16px 16px 0 0;
  position: fixed;
  top: auto;
  left: 50%;
  right: auto;
  bottom: env(safe-area-inset-bottom, 0);
  transform-origin: bottom center;
  /* Start off-screen bottom and center horizontally */
  transform: translate(-50%, 100%);
  transition: transform 450ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

.base-modal[open].is-open[id$="-modal"][id*="-tour"] {
  transform: translate(-50%, 0);
}

/* Backdrop fade for tour modals (pure CSS) */
.base-modal[id$="-modal"][id*="-tour"]::backdrop {
  opacity: 0;
  transition: opacity 450ms ease-out;
}
.base-modal[open][id$="-modal"][id*="-tour"]::backdrop {
  opacity: var(--base-modal-backdrop-opacity);
}
</style>
